import React, { useEffect, useState } from 'react';
import { Input } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';
import { parseSahApiError } from '~support-at-home/lib/sahApiError';

import { Button, ErrorResponse, Select, Spinner } from '~components';

import { ClaimItem } from '~support-at-home/models';

import { useGetSahLateSubmissionReasonsQuery } from '~lookups/actions/sahLateSubmissionReasons';
import { useUpdateClaimItemLateSubmissionMutation } from '~support-at-home/actions/claims';

interface ComponentProps {
  itemId: string;
  /** Claim the item belongs to - see the equivalent note on ClaimItemRequirements. */
  claimId?: string;
  row: ClaimItem;
  /**
   * Whether the parent ClaimItemRequirements modal is open. Gates the reasons lookup query the
   * same way the old trigger+modal's own `isOpen` state did, and drives the initial sync of the
   * form fields from `row` below (the old component did this in a click handler; here the modal
   * click is owned by the parent, so the panel syncs off the prop instead).
   */
  isOpen: boolean;
}

/**
 * ClaimItemLateSubmissionPanel
 *
 * Late submission reason tab content for ClaimItemRequirements (TAV-2592) - the reason `Select`,
 * conditional justification textarea and save button, extracted from the old
 * ClaimItemLateSubmission trigger+modal (TAV-2591/2592 predecessor). This component owns no modal
 * or trigger of its own; the parent renders it as one `Tab.content` inside a shared `TabContainer`.
 */
const ClaimItemLateSubmissionPanel: React.FC<ComponentProps> = ({ itemId, claimId, row, isOpen }) => {
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [justification, setJustification] = useState('');

  const { showToast } = useToast();

  const {
    data: reasons,
    isFetching: isReasonsLoading,
    error: reasonsError,
  } = useGetSahLateSubmissionReasonsQuery(undefined, { skip: !isOpen });
  const [updateLateSubmission, { isLoading: isSaving }] = useUpdateClaimItemLateSubmissionMutation();

  // Syncs the form to the row's captured reason whenever the panel opens (mirrors the old
  // component's handleOpen, which ran from the trigger's own onClick) or whenever those two fields
  // change while the panel is already open (e.g. a claim refetch after a save elsewhere).
  useEffect(() => {
    if (isOpen) {
      setSelectedCode(row.late_submission_reason_code ?? null);
      setJustification(row.late_submission_justification ?? '');
    }
  }, [isOpen, row.late_submission_reason_code, row.late_submission_justification]);

  const reasonOptions = (reasons ?? []).map((reason) => ({ value: reason.sah_id, label: reason.name }));
  const selectedReason = (reasons ?? []).find((reason) => reason.sah_id === selectedCode) ?? null;
  // AC #7, data-driven: never a hardcoded "Other" string match. Four codes carry
  // `free_text_required: true` (CULTURAL_LEAVE, DECEASED_CARE_RECIPIENT, FIRST_NATIONS_PEOPLE,
  // OTHER), so a string match would miss three of them.
  const justificationRequired = Boolean(selectedReason?.free_text_required);
  // Saving requires the reasons list to be IN HAND, not merely "no longer loading". Both conjuncts
  // below are load-bearing and neither implies the other.
  //
  // `justificationRequired` is derived from `reasons`, but `selectedCode` is populated synchronously
  // from the row by the effect above - so whenever `reasons` is absent while a code is already
  // selected, the requirement reads as false, the textarea is not rendered, and Save would send
  // `late_submission_justification: null`. The API rejects that outright ("A justification is
  // required for the selected late submission reason"). Three distinct states leave `reasons`
  // undefined - the first load in flight, the lookup having FAILED (isFetching goes false and the
  // data never arrives; the ErrorResponse below is what the user sees instead), and the query being
  // skipped - so the guard has to be on the data, not on the loading flag.
  //
  // `!isReasonsLoading` then closes the remaining window: RTK Query serves the PREVIOUS list while a
  // refetch is in flight, so `reasons` can be defined but mid-replacement.
  //
  // Clearing is the one path that bypasses all of that. `late_submission_reason_code: null` nulls
  // both columns server-side and sends no code, so it needs nothing from the lookup - which also
  // means a captured reason stays removable even when the lookup has failed. Gated on the row
  // actually HOLDING a code so Save is not live on an untouched, empty form.
  const isClearing = !selectedCode && Boolean(row.late_submission_reason_code);
  const canSave =
    isClearing ||
    (reasons !== undefined &&
      !isReasonsLoading &&
      Boolean(selectedCode) &&
      (!justificationRequired || justification.trim().length > 0));

  const handleSave = async () => {
    if (!canSave) return;

    try {
      await updateLateSubmission({
        id: itemId,
        claimId,
        late_submission_reason_code: selectedCode,
        late_submission_justification: justificationRequired ? justification.trim() : null,
      }).unwrap();
      showToast('success', isClearing ? 'Late submission reason removed' : 'Late submission reason saved');
    } catch (error) {
      showToast('danger', parseSahApiError(getErrorMessage(error)));
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div>
        {/* The spinner sits ON the label row so it reads as "this field is loading" rather than as a
            page-level state. Disabling the Select alone left no positive signal that anything was in
            flight - an empty, greyed dropdown looks the same as one with no options to offer. */}
        <div className="d-flex align-items-center gap-2">
          <label htmlFor={`claim-item-late-submission-reason-${itemId}`} className="form-label mb-0">
            Reason
          </label>
          {isReasonsLoading && <Spinner loading size={14} />}
        </div>
        <Select
          id={`claim-item-late-submission-reason-${itemId}`}
          name="late_submission_reason_code"
          value={selectedCode}
          options={reasonOptions}
          onChange={(_, value) => setSelectedCode((value as string) ?? null)}
          placeholder="Select a reason"
          ariaLabel="Late submission reason"
          disabled={isReasonsLoading}
          // The API accepts a null code to clear a captured reason, and until now nothing in the UI
          // could send one. It matters most exactly where this panel exists: after finalise, where a
          // mis-clicked reason has no other undo.
          isClearable
        />
      </div>
      {justificationRequired && (
        <div>
          <label htmlFor={`claim-item-late-submission-justification-${itemId}`} className="form-label">
            Justification
          </label>
          <Input
            id={`claim-item-late-submission-justification-${itemId}`}
            type="textarea"
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
            aria-label="Late submission justification"
            style={{ minHeight: 80 }}
          />
        </div>
      )}
      <ErrorResponse message={reasonsError ? getErrorMessage(reasonsError) : null} />
      <div className="d-flex justify-content-end">
        <Button
          color="success"
          disabled={!canSave || isSaving}
          loading={isSaving}
          // Button's `loading` prop replaces children with a spinner, which loses the button's
          // accessible name - an explicit aria-label keeps `getByRole`/screen readers working.
          aria-label="Save late submission reason"
          onClick={handleSave}
          dataCy={`claim-item-late-submission-save-button-${itemId}`}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default ClaimItemLateSubmissionPanel;
